import React, { useState } from 'react'
import { useLocation } from 'wouter'
import * as UI from '@mantine/core'
import { useForm } from '@mantine/form'
import QRCode from 'react-qr-code'

import Logo from '../../resources/icons/logotype-bleu-fonce.png'

import { graphql } from '../../services/graphql'
import { getDeviceToken, rememberDevice } from '../../services/session'

import { buildComponent } from '../../components/factory'
import { useStore } from '../../hooks/state'

// Étapes de la connexion. Le second facteur (#7512) se joue dans le MÊME
// écran : l'utilisateur n'est jamais « à moitié connecté » ailleurs.
type Step = 'credentials' | 'totp' | 'setup'

type LoginError =
  | ''
  | 'user_not_found'
  | 'wrong_credentials'
  | 'internal_server_error'
  | 'invalid_code'
  | 'locked'
  | 'totp_already_enabled'
  | 'not_allowed'
  | 'not_found'

const CODE_LENGTH = 6

// La clé base32 est recopiée à la main quand l'appareil photo ne coopère pas.
// La grouper par 4 divise par deux les erreurs de saisie, comme un IBAN ou un
// numéro de carte.
const grouper = (cle: string) => (cle.match(/.{1,4}/g) || []).join(' ')

// Branding à gauche, formulaire à droite. Utilisé sur les écrans courts (où il
// existait déjà) et pour l'étape d'enrôlement dès qu'il y a la largeur.
const deuxColonnes = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  maxWidth: '1040px',
  gap: '6rem',
  '& > *': {
    flex: 1,
  },
}

export const ScreenDisconnectedLogin = buildComponent<{
  // Posé par le routeur quand la session Firebase n'a pas pu s'ouvrir alors
  // que le jeton API était valide. Sans ce signal, l'utilisateur revenait sur
  // un formulaire vide sans savoir que sa connexion avait pourtant abouti.
  interrupted?: boolean
}>()
  .withLocale('screens.disconnected.login.screen')
  .withLifecycle(() => {
    const Store = useStore()
    const [, setLocation] = useLocation()

    const [error, setError] = useState<LoginError>('')
    const [submitting, setSubmitting] = useState(false)
    const [step, setStep] = useState<Step>('credentials')

    // Le défi vit UNIQUEMENT ici, jamais dans localStorage : `restore()` le
    // reprendrait pour une session, `administratorAuthenticated` le refuserait,
    // et l'application resterait bloquée sur un écran de chargement.
    const [challenge, setChallenge] = useState<string | null>(null)
    const [enrollment, setEnrollment] = useState<{
      otpauthUrl: string
      secret: string
    } | null>(null)

    const [code, setCode] = useState('')
    const [remember, setRemember] = useState(false)
    const [copied, setCopied] = useState(false)
    const [lockedUntil, setLockedUntil] = useState<number | null>(null)

    const form = useForm({
      initialValues: {
        email: '',
        password: '',
      },
    })

    const fail = (err: unknown) => {
      console.error(err)
      setError('internal_server_error')
      setSubmitting(false)
    }

    const connect = (token: string) => {
      Store.getActions().utils.session.connectWithToken(token)
    }

    // Prépare un secret et bascule sur l'écran QR.
    const startEnrollment = (forChallenge: string | null) =>
      graphql.mutations
        .totpEnroll(forChallenge)
        .then(({ administratorTotpEnroll }) => {
          if (
            administratorTotpEnroll.error ||
            !administratorTotpEnroll.otpauthUrl ||
            !administratorTotpEnroll.secret
          ) {
            setError((administratorTotpEnroll.error || '') as LoginError)
            setSubmitting(false)
            return
          }

          setEnrollment({
            otpauthUrl: administratorTotpEnroll.otpauthUrl,
            secret: administratorTotpEnroll.secret,
          })
          setStep('setup')
          setSubmitting(false)
        })

    const handleSubmit = form.onSubmit((values) => {
      setError('')
      setSubmitting(true)

      graphql.queries
        .login(values.email, values.password, getDeviceToken())
        .then(({ administratorLogin }) => {
          if (administratorLogin.error) {
            setError(administratorLogin.error)
            setSubmitting(false)
            return
          }

          // Appareil de confiance, ou compte client : session immédiate.
          if (administratorLogin.token) {
            connect(administratorLogin.token)
            return
          }

          setChallenge(administratorLogin.challenge)
          setCode('')

          if (administratorLogin.status === 'totp_setup_required') {
            return startEnrollment(administratorLogin.challenge)
          }

          setStep('totp')
          setSubmitting(false)
          return undefined
        })
        .catch(fail)
    })

    const handleVerify = (event: React.FormEvent) => {
      event.preventDefault()

      // Saisie encore incomplète (Entrée pressée dans le champ, alors que le
      // bouton est désactivé) : on ne reproche rien, il n'a pas fini.
      if (code.length < CODE_LENGTH) {
        return
      }

      // Le service GraphQL interpole ses arguments sans échappement : on ne
      // laisse partir que six chiffres.
      if (!/^[0-9]{6}$/.test(code)) {
        setError('invalid_code')
        return
      }

      setError('')
      setSubmitting(true)

      graphql.mutations
        .totpVerify(code, challenge, remember)
        .then(({ administratorTotpVerify }) => {
          if (administratorTotpVerify.error) {
            setError(administratorTotpVerify.error)
            setLockedUntil(administratorTotpVerify.lockedUntil)
            setCode('')
            setSubmitting(false)
            return
          }

          rememberDevice(administratorTotpVerify.deviceToken)

          if (administratorTotpVerify.token) {
            connect(administratorTotpVerify.token)
          }
        })
        .catch(fail)
    }

    const goToCredentials = () => {
      setStep('credentials')
      setChallenge(null)
      setEnrollment(null)
      setCode('')
      setError('')
      setLockedUntil(null)
      setSubmitting(false)
    }

    return {
      form,
      handleSubmit,
      handleVerify,
      goToCredentials,
      error,
      // Minutes restantes du verrouillage, pour l'interpolation du message.
      lockedMinutes: lockedUntil
        ? Math.max(1, Math.ceil((lockedUntil - Date.now()) / 60000))
        : 0,
      submitting,
      step,
      enrollment,
      code,
      setCode,
      remember,
      setRemember,
      copied,
      copySecret: () => {
        if (!enrollment) {
          return
        }
        navigator.clipboard?.writeText(enrollment.secret).catch(() => {})
        setCopied(true)
      },
      goToReset: () => setLocation('/password-reset'),
    }
  })
  .withRender(({ lifecycle, locale, props }) => {
    const interruption = props.interrupted && !lifecycle.error && (
      <UI.Alert mt="md" color="orange">
        <UI.Text size="sm">{locale('error.handshake_interrupted')}</UI.Text>
      </UI.Alert>
    )

    const alert = lifecycle.error && (
      <UI.Alert mt="md" color="red">
        <UI.Text color="red">
          {locale(`error.${lifecycle.error}`, {
            minutes: lifecycle.lockedMinutes,
          })}
        </UI.Text>
      </UI.Alert>
    )

    // Champ commun aux deux étapes de code (saisie initiale et enrôlement).
    // Pas de UI.PinInput : il n'existe pas en Mantine 5.5.
    const codeField = (
      <>
        <UI.TextInput
          autoFocus
          disabled={lifecycle.submitting}
          value={lifecycle.code}
          onChange={(event) =>
            lifecycle.setCode(
              event.currentTarget.value
                .replace(/[^0-9]/g, '')
                .slice(0, CODE_LENGTH),
            )
          }
          label={locale('totp.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          mt="md"
          sx={{ input: { letterSpacing: '0.4rem', fontSize: '1.8rem' } }}
        />

        <UI.Checkbox
          mt="md"
          checked={lifecycle.remember}
          onChange={(event) =>
            lifecycle.setRemember(event.currentTarget.checked)
          }
          label={locale('totp.remember')}
        />

        {alert}
        {interruption}

        <UI.Button
          disabled={lifecycle.submitting || lifecycle.code.length < CODE_LENGTH}
          type="submit"
          fullWidth
          mt="xl"
        >
          {locale('totp.verify')}
        </UI.Button>

        <UI.Anchor
          component="button"
          type="button"
          size="sm"
          color="dimmed"
          mt="lg"
          sx={{ display: 'block', margin: '1.5rem auto 0' }}
          onClick={lifecycle.goToCredentials}
        >
          {locale('back')}
        </UI.Anchor>
      </>
    )

    return (
      <UI.Box
        sx={(theme) => ({
          width: '100%',
          // `minHeight` et non `height` : les styles globaux de Mantine figent
          // html/body/#root à la hauteur de la fenêtre, donc `height: 100%`
          // bloquait cette boîte à cette taille. L'étape d'enrôlement (QR code
          // + clé + champ code) est plus haute que le formulaire de connexion
          // pour lequel la mise en page avait été conçue : le contenu
          // s'échappait de la boîte et se peignait par-dessus les deux bandes
          // bleues, qui sont des bordures. Avec `minHeight`, la boîte grandit
          // et la page défile normalement.
          minHeight: '100%',
          borderTop: '4rem solid ' + theme.colors.blue[7],
          borderBottom: '8rem solid ' + theme.colors.blue[7],
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        })}
      >
        <UI.Container
          size={lifecycle.step === 'setup' ? 1040 : 420}
          my={lifecycle.step === 'setup' ? 24 : 40}
          sx={{
            '@media(max-height: 800px)': deuxColonnes,
            // L'étape d'enrôlement empile QR + clé + code : en une seule
            // colonne elle dépasse la hauteur d'écran et oblige à faire
            // défiler. Dès qu'il y a la largeur, on met le texte explicatif à
            // gauche et le formulaire à droite — la hauteur est divisée par
            // deux et tout tient d'un coup d'œil.
            ...(lifecycle.step === 'setup'
              ? { '@media(min-width: 900px)': deuxColonnes }
              : {}),
          }}
        >
          <UI.Stack spacing={0} justify="center">
            <UI.Title align="center" title="Nap&Up">
              <UI.Image mx="auto" mb={32} width={128} src={Logo} alt="Nap&Up" />
            </UI.Title>
            <UI.Title
              order={2}
              align="center"
              sx={() => ({
                fontWeight: 900,
              })}
            >
              {lifecycle.step === 'credentials'
                ? locale('subtitle')
                : locale(
                    lifecycle.step === 'setup' ? 'setup.title' : 'totp.title',
                  )}
            </UI.Title>

            <UI.Text mt={16} align="center" color="dimmed">
              {lifecycle.step === 'credentials'
                ? locale('description')
                : locale(
                    lifecycle.step === 'setup'
                      ? 'setup.description'
                      : 'totp.description',
                  )}
            </UI.Text>
          </UI.Stack>

          <UI.Stack spacing={0} justify="center">
            <form
              onSubmit={
                lifecycle.step === 'credentials'
                  ? lifecycle.handleSubmit
                  : lifecycle.handleVerify
              }
            >
              <UI.Paper
                withBorder
                px={32}
                py={lifecycle.step === 'setup' ? 28 : 48}
                mt={32}
                mb="5vh"
                radius="md"
                sx={{
                  '@media(max-height: 800px)': {
                    marginBottom: '32px',
                  },
                }}
              >
                {lifecycle.step === 'credentials' && (
                  <>
                    <UI.TextInput
                      disabled={lifecycle.submitting}
                      {...lifecycle.form.getInputProps('email')}
                      label={locale('email')}
                    />
                    <UI.PasswordInput
                      disabled={lifecycle.submitting}
                      {...lifecycle.form.getInputProps('password')}
                      label={locale('password')}
                      mt="md"
                    />

                    {alert}

                    <UI.Button
                      disabled={lifecycle.submitting}
                      type="submit"
                      fullWidth
                      mt="xl"
                    >
                      {locale('login')}
                    </UI.Button>

                    <UI.Anchor
                      component="button"
                      type="button"
                      size="sm"
                      color="dimmed"
                      mt="lg"
                      sx={{ display: 'block', margin: '1.5rem auto 0' }}
                      onClick={lifecycle.goToReset}
                    >
                      Mot de passe oublié ?
                    </UI.Anchor>
                  </>
                )}

                {lifecycle.step === 'setup' && lifecycle.enrollment && (
                  <>
                    <UI.Box
                      sx={(theme) => ({
                        background: 'white',
                        border: `1px solid ${theme.colors.gray[3]}`,
                        borderRadius: theme.radius.md,
                        padding: 12,
                        display: 'flex',
                        justifyContent: 'center',
                      })}
                    >
                      <QRCode
                        value={lifecycle.enrollment.otpauthUrl}
                        size={180}
                      />
                    </UI.Box>

                    <UI.Text size="sm" color="dimmed" mt={16} mb={6}>
                      {locale('setup.manual')}
                    </UI.Text>

                    {/* La clé et son bouton occupent toute la ligne : c'est
                        l'élément de repli quand le scan échoue, il doit se lire
                        d'un coup d'œil et se copier sans viser. */}
                    <UI.Group
                      position="apart"
                      spacing={8}
                      noWrap
                      sx={(theme) => ({
                        border: `1px solid ${theme.colors.gray[3]}`,
                        borderRadius: theme.radius.md,
                        backgroundColor: theme.colors.gray[0],
                        padding: '4px 4px 4px 12px',
                      })}
                    >
                      <UI.Text
                        sx={{
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, monospace',
                          // Chiffres tabulaires : la clé ne doit pas gigoter.
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '1.5rem',
                          letterSpacing: '0.08em',
                          whiteSpace: 'nowrap',
                          overflowX: 'auto',
                        }}
                      >
                        {grouper(lifecycle.enrollment.secret)}
                      </UI.Text>
                      <UI.Button
                        size="sm"
                        variant={lifecycle.copied ? 'filled' : 'light'}
                        color={lifecycle.copied ? 'green' : 'blue'}
                        type="button"
                        onClick={lifecycle.copySecret}
                        sx={{ flexShrink: 0, minHeight: 44 }}
                      >
                        {locale(
                          lifecycle.copied ? 'setup.copied' : 'setup.copy',
                        )}
                      </UI.Button>
                    </UI.Group>

                    <UI.Text size="xs" color="dimmed" mt={6}>
                      {locale('setup.once')}
                    </UI.Text>

                    {codeField}
                  </>
                )}

                {lifecycle.step === 'totp' && codeField}
              </UI.Paper>
            </form>
          </UI.Stack>
        </UI.Container>
      </UI.Box>
    )
  })
